import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserPlus,
  Student as StudentIcon,
  MagnifyingGlass,
  DownloadSimple,
  UploadSimple,
  Trash,
  ArrowRight,
  PencilSimple,
  EnvelopeSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError, useAuth } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function StudentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [availableSchools, setAvailableSchools] = useState([]);
  const [importSchools, setImportSchools] = useState([]);
  const [loadingImportOptions, setLoadingImportOptions] = useState(true);
  const [importOptionsError, setImportOptionsError] = useState("");
  const [importStudentSchoolIds, setImportStudentSchoolIds] = useState([]);
  const [newSchoolIds, setNewSchoolIds] = useState([]);
  const [classId, setClassId] = useState("all");
  const [importSchoolId, setImportSchoolId] = useState("");
  const [students, setStudents] = useState([]);
  const [page, setPage] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const studentRequestRef = useRef(0);
  const [search, setSearch] = useState("");
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");
  const [email, setEmail] = useState("");
  const [newClassId, setNewClassId] = useState("");
  const [importingMode, setImportingMode] = useState("");
  const [importSummary, setImportSummary] = useState(null);
  const [resolutionDialog, setResolutionDialog] = useState(null);
  const [classResolutions, setClassResolutions] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCpf, setEditCpf] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editSchoolIds, setEditSchoolIds] = useState([]);
  const [editClassIds, setEditClassIds] = useState([]);
  const [savingStudent, setSavingStudent] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [creatingAccounts, setCreatingAccounts] = useState(false);
  const [sendingInvitations, setSendingInvitations] = useState(false);
  const [resendingStudentId, setResendingStudentId] = useState("");
  const [exportingMissingEmails, setExportingMissingEmails] = useState(false);
  const [importingEmails, setImportingEmails] = useState(false);
  const fileRef = useRef(null);
  const simpleFileRef = useRef(null);
  const emailFileRef = useRef(null);
  const schoolSelectionRef = useRef(null);

  const activeClassId = classId === "all" ? "" : classId;
  const importing = Boolean(importingMode);
  const selectedClass = classes.find((item) => item.id === activeClassId);
  const invitationScope = selectedStudentIds.length
    ? students.filter((item) => selectedStudentIds.includes(item.id))
    : students;
  const selectedPendingCpfCount = selectedStudentIds.filter((id) => (
    students.find((item) => item.id === id)?.cpf_pending
  )).length;
  const selectedAccountStudentIds = selectedStudentIds.filter((id) => !(
    students.find((item) => item.id === id)?.cpf_pending
  ));
  const invitationStudentIds = invitationScope
    .filter((item) => !item.cpf_pending && item.has_user_account && !item.user_account_active
      && item.email && !item.student_invitation_sent_at)
    .map((item) => item.id);

  const load = useCallback(async () => {
    setLoadingImportOptions(true);
    setImportOptionsError("");
    try {
      const [c, options] = await Promise.all([
        api.get("/classes"),
        api.get("/students/import-options"),
      ]);
      setClasses(c.data);
      const available = options.data?.available_schools || [];
      const manageable = options.data?.manageable_schools || [];
      const autoSchoolId = options.data?.auto_school_id || "";
      setAvailableSchools(available);
      setImportSchools(manageable);
      setImportStudentSchoolIds((current) => {
        const valid = current.filter((id) => available.some((school) => school.id === id));
        return valid.length ? valid : (autoSchoolId ? [autoSchoolId] : available.length === 1 ? [available[0].id] : []);
      });
      setNewSchoolIds((current) => {
        const valid = current.filter((id) => available.some((school) => school.id === id));
        return valid.length ? valid : (autoSchoolId ? [autoSchoolId] : []);
      });
      setImportSchoolId((current) => {
        if (current && manageable.some((school) => school.id === current)) return current;
        return manageable.length === 1 ? manageable[0].id : "";
      });
    } catch (e) {
      setImportOptionsError("Não foi possível carregar as escolas. Tente novamente.");
      toast.error(fmtError(e));
    } finally {
      setLoadingImportOptions(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openImportFile = (identityMode = "strict") => {
    if (importing || loadingImportOptions) return;
    if (importOptionsError) { toast.error(importOptionsError); return; }
    if (!importStudentSchoolIds.length) {
      toast.error("Selecione a escola dos alunos antes de importar.");
      schoolSelectionRef.current?.scrollIntoView?.({ block: "center", behavior: "smooth" });
      schoolSelectionRef.current?.focus();
      return;
    }
    (identityMode === "name_class_only" ? simpleFileRef : fileRef).current?.click();
  };

  const refreshStudents = useCallback(async () => {
    const request = ++studentRequestRef.current;
    setLoadingStudents(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page)); params.set("page_size", "100");
      if (activeClassId) params.set("class_id", activeClassId);
      if (search) params.set("q", search);
      const query = params.toString();
      const r = await api.get(`/students${query ? `?${query}` : ""}`);
      if (request !== studentRequestRef.current) return;
      const rows = Array.isArray(r.data) ? r.data : r.data.items || [];
      const total = Array.isArray(r.data) ? rows.length : r.data.total || 0;
      if (page > 1 && page > Math.max(1, Math.ceil(total / 100))) {
        setPage(Math.max(1, Math.ceil(total / 100))); return;
      }
      setStudents(rows); setTotalStudents(total);
      setSelectedStudentIds((current) => current.filter((id) => rows.some((item) => item.id === id)));
    } catch (e) {
      if (request !== studentRequestRef.current) return;
      setStudents([]);
      setTotalStudents(0);
      toast.error(fmtError(e));
    } finally {
      if (request === studentRequestRef.current) setLoadingStudents(false);
    }
  }, [activeClassId, search, page]);

  useEffect(() => { refreshStudents(); return () => { studentRequestRef.current += 1; }; }, [refreshStudents]);

  const resetStudentFilter = (setter, value) => {
    studentRequestRef.current += 1; setStudents([]); setSelectedStudentIds([]);
    setLoadingStudents(true); setPage(1); setter(value);
  };

  const studentPagination = <div className="flex items-center justify-between gap-3 my-4" aria-label="Paginação de alunos">
    <span className="text-sm text-muted-foreground">{totalStudents.toLocaleString("pt-BR")} alunos · página {page} de {Math.max(1, Math.ceil(totalStudents / 100))}</span>
    <div className="flex gap-2">
      <Button variant="outline" className="rounded-none" disabled={loadingStudents || page <= 1} onClick={() => { setSelectedStudentIds([]); setPage(page - 1); }}>Anterior</Button>
      <Button variant="outline" className="rounded-none" disabled={loadingStudents || page * 100 >= totalStudents} onClick={() => { setSelectedStudentIds([]); setPage(page + 1); }}>Próxima</Button>
    </div>
  </div>;

  const handleNewDialog = (value) => {
    setOpen(value);
    if (value) {
      setNewClassId(activeClassId || "");
      if (availableSchools.length === 1) setNewSchoolIds([availableSchools[0].id]);
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudentIds((current) => current.includes(studentId)
      ? current.filter((value) => value !== studentId)
      : [...current, studentId]);
  };

  const createSelectedAccounts = async () => {
    if (!selectedAccountStudentIds.length) {
      toast.warning("Informe o CPF dos alunos provisórios antes de criar o acesso deles");
      return;
    }
    setCreatingAccounts(true);
    try {
      const response = await api.post("/students/accounts/bulk", { student_ids: selectedAccountStudentIds });
      const result = response.data;
      toast.success(`${result.processed} aluno(s) processado(s) · ${result.accounts_created} usuário(s) criado(s) · nenhum e-mail enviado`);
      if (result.without_email_count) toast.warning(`${result.without_email_count} aluno(s) sem e-mail não puderam receber usuário`);
      const pendingCount = selectedPendingCpfCount + (result.pending_cpf_count || 0);
      if (pendingCount) toast.warning(`${pendingCount} aluno(s) provisório(s) ficaram sem acesso até informar o CPF`);
      if (result.error_count) result.errors.slice(0, 5).forEach((item) => toast.error(`${item.name}: ${item.error}`));
      setSelectedStudentIds([]);
      await refreshStudents();
    } catch (error) { toast.error(fmtError(error)); }
    finally { setCreatingAccounts(false); }
  };

  const sendPendingInvitations = async () => {
    if (!invitationStudentIds.length) return;
    const scope = selectedStudentIds.length
      ? `${invitationStudentIds.length} aluno(s) selecionado(s)`
      : `${invitationStudentIds.length} aluno(s) pendente(s) desta lista`;
    if (!window.confirm(`Enviar agora o e-mail de confirmação para ${scope}?`)) return;
    setSendingInvitations(true);
    try {
      const response = await api.post("/students/accounts/invitations/bulk", {
        student_ids: invitationStudentIds,
      });
      const result = response.data || {};
      toast.success(`${result.invitations_sent || 0} convite(s) enviado(s)`);
      if (result.failed) toast.error(`${result.failed} envio(s) não foram confirmados pelo serviço de e-mail`);
      if (result.errors?.length) result.errors.slice(0, 5).forEach((item) => toast.error(`${item.name}: ${item.error}`));
      setSelectedStudentIds([]);
      await refreshStudents();
    } catch (error) { toast.error(fmtError(error)); }
    finally { setSendingInvitations(false); }
  };

  const resendStudentConfirmation = async (student) => {
    if (!student?.id) return;
    if (!window.confirm(`Reenviar a confirmação para ${student.name}?`)) return;
    setResendingStudentId(student.id);
    try {
      const response = await api.post(`/admin/students/${student.id}/resend-confirmation`);
      const kind = response.data?.delivery_kind;
      toast.success(kind === "verification_code"
        ? "Novo código de 6 dígitos enviado"
        : "Convite de confirmação reenviado");
      await refreshStudents();
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setResendingStudentId("");
    }
  };

  const toggleSchool = (setter, id) => {
    setter((current) => current.includes(id)
      ? current.filter((value) => value !== id)
      : [...current, id]);
  };

  const create = async () => {
    if (!newSchoolIds.length) {
      toast.error("Selecione ao menos uma escola para o aluno");
      return;
    }
    try {
      const response = await api.post("/students", {
        name,
        cpf: cpf.replace(/\D/g, ""),
        email: email.trim() || null,
        school_ids: newSchoolIds,
      });
      if (newClassId) {
        await api.post("/enrollments", { student_id: response.data.id, class_id: newClassId });
      }
      toast.success(response.data.reused
        ? "Aluno existente vinculado à escola" + (newClassId ? " e à turma" : "")
        : "Aluno cadastrado e vinculado à escola" + (newClassId ? " e à turma" : ""));
      setOpen(false);
      setName("");
      setCpf("");
      setEmail("");
      setNewClassId("");
      if (availableSchools.length !== 1) setNewSchoolIds([]);
      refreshStudents();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const downloadTemplate = async (identityMode = "strict") => {
    const simple = identityMode === "name_class_only";
    try {
      const res = await api.get(
        simple ? "/students/template.xlsx?mode=simple" : "/students/template.xlsx",
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = simple ? "template_alunos_nome_turma.xlsx" : "template_alunos_com_turma.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(fmtError(e)); }
  };

  const downloadMissingEmails = async () => {
    setExportingMissingEmails(true);
    try {
      const params = new URLSearchParams();
      if (activeClassId) params.set("class_id", activeClassId);
      if (search.trim()) params.set("q", search.trim());
      const query = params.toString();
      const res = await api.get(
        `/students/missing-email.xlsx${query ? `?${query}` : ""}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "alunos_sem_email.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Arquivo dos alunos sem e-mail gerado para o filtro selecionado");
    } catch (e) { toast.error(fmtError(e)); }
    finally { setExportingMissingEmails(false); }
  };

  const importStudentEmails = async (file) => {
    setImportingEmails(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/students/email-import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const result = res.data || {};
      toast.success(
        `${result.emails_updated || 0} e-mail(s) atualizado(s) · ` +
        `${result.accounts_created || 0} usuário(s) criado(s) · nenhum convite enviado`,
        { duration: 7000 },
      );
      if (result.unfilled) toast.warning(`${result.unfilled} linha(s) sem e-mail foram ignoradas`);
      if (result.errors?.length) {
        result.errors.slice(0, 5).forEach((item) => (
          toast.error(`Linha ${item.row}${item.name ? ` · ${item.name}` : ""}: ${item.error}`, { duration: 8000 })
        ));
      }
      await refreshStudents();
    } catch (e) { toast.error(fmtError(e)); }
    finally {
      setImportingEmails(false);
      if (emailFileRef.current) emailFileRef.current.value = "";
    }
  };

  const importXlsx = async (file, resolutions = null, identityMode = "strict") => {
    if (!file || importing) return;
    if (!importStudentSchoolIds.length) {
      toast.error("Selecione a escola dos alunos antes de importar.");
      return;
    }
    const simple = identityMode === "name_class_only";
    setImportingMode(identityMode);
    let preserveFile = false;
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("student_school_ids", JSON.stringify(importStudentSchoolIds));
      if (resolutions) form.append("class_resolutions", JSON.stringify(resolutions));
      const params = new URLSearchParams();
      if (activeClassId) params.set("class_id", activeClassId);
      if (importSchoolId) params.set("school_id", importSchoolId);
      if (simple) params.set("simple_roster", "true");
      const query = params.toString();
      const res = await api.post(
        `/students/import${query ? `?${query}` : ""}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const {
        created, matched_by_cpf: matchedByCpf, matched_by_name: matchedByName,
        matched_by_name_in_class: matchedByNameInClass,
        created_without_cpf: createdWithoutCpf,
        enrolled, already_enrolled: alreadyEnrolled, linked_schools: linkedSchools,
        created_classes: createdClasses, skipped, errors, warnings,
      } = res.data;
      const reused = (matchedByCpf || 0) + (matchedByName || 0) + (matchedByNameInClass || 0);
      setImportSummary({
        simple,
        created: created || 0,
        reused,
        enrolled: enrolled || 0,
        createdWithoutCpf: createdWithoutCpf || 0,
        skipped: skipped || 0,
        errors: errors || [],
        warnings: warnings || [],
      });
      toast.success(
        `Importação concluída: ${created} novo(s), ${reused} reutilizado(s), ${enrolled} vínculo(s) criado(s)` +
        `${alreadyEnrolled ? `, ${alreadyEnrolled} já existente(s)` : ""}` +
        `${linkedSchools ? `, ${linkedSchools} vínculo(s) com escola` : ""}` +
        `${skipped ? `, ${skipped} linha(s) ignorada(s)` : ""}`,
        { duration: 7000 },
      );
      if (simple && createdWithoutCpf) {
        toast.message(`${createdWithoutCpf} aluno(s) foram cadastrados sem CPF e sem conta de acesso`, { duration: 7000 });
      }
      if (createdClasses?.length) {
        toast.success(`Turma(s) criada(s): ${createdClasses.map((item) => item.name).join(", ")}`, { duration: 7000 });
      }
      if (warnings?.length) warnings.slice(0, 3).forEach(message => toast.warning(message, { duration: 7000 }));
      if (errors?.length) errors.slice(0, 5).forEach(message => toast.error(message, { duration: 8000 }));
      setResolutionDialog(null);
      setClassResolutions({});
      await load();
      await refreshStudents();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "class_resolution_required") {
        preserveFile = true;
        const available = detail.available_classes || [];
        const schools = detail.manageable_schools || [];
        const initial = {};
        (detail.issues || []).forEach((issue) => {
          const preferred = available.find((item) => (issue.candidate_class_ids || []).includes(item.id))
            || available.find((item) => item.id === activeClassId)
            || available[0];
          const preferredSchool = schools.find((item) => item.id === importSchoolId) || schools[0];
          if (issue.reason === "missing" && preferredSchool) {
            initial[issue.key] = { action: "create", school_id: preferredSchool.id };
          } else if (preferred) {
            initial[issue.key] = { action: "existing", class_id: preferred.id };
          } else if (preferredSchool) {
            initial[issue.key] = { action: "create", school_id: preferredSchool.id };
          } else {
            initial[issue.key] = { action: "existing", class_id: "" };
          }
        });
        setClassResolutions(initial);
        setResolutionDialog({
          file,
          identityMode,
          message: detail.message,
          issues: detail.issues || [],
          availableClasses: available,
          manageableSchools: schools,
        });
      } else {
        toast.error(fmtError(e));
      }
    } finally {
      setImportingMode("");
      if (!preserveFile) {
        if (simple && simpleFileRef.current) simpleFileRef.current.value = "";
        if (!simple && fileRef.current) fileRef.current.value = "";
      }
    }
  };

  const updateResolution = (key, patch) => {
    setClassResolutions((current) => ({
      ...current,
      [key]: { ...(current[key] || {}), ...patch },
    }));
  };

  const resolutionsValid = resolutionDialog?.issues?.every((issue) => {
    const resolution = classResolutions[issue.key];
    if (!resolution) return false;
    if (resolution.action === "existing") return Boolean(resolution.class_id);
    if (resolution.action === "create") return Boolean(resolution.school_id);
    return false;
  });

  const continueImport = async () => {
    if (!resolutionDialog?.file || !resolutionsValid) return;
    await importXlsx(
      resolutionDialog.file,
      classResolutions,
      resolutionDialog.identityMode || "strict",
    );
  };

  const cancelResolution = () => {
    setResolutionDialog(null);
    setClassResolutions({});
    if (fileRef.current) fileRef.current.value = "";
    if (simpleFileRef.current) simpleFileRef.current.value = "";
  };

  const runDelete = async (permanent) => {
    if (!deleteDialog) return;
    if (!permanent && !activeClassId) {
      toast.error("Selecione uma turma para remover o vínculo");
      return;
    }
    setDeleting(true);
    try {
      const params = new URLSearchParams({ permanent: String(permanent) });
      if (activeClassId) params.set("class_id", activeClassId);
      const endpoint = deleteDialog.mode === "all"
        ? `/students?${params}`
        : `/students/${deleteDialog.student.id}?${params}`;
      const res = await api.delete(endpoint);
      const removed = res.data?.deleted_enrollments || 0;
      const deleted = res.data?.deleted_students || 0;
      if (permanent) {
        toast.success(`${deleted} aluno(s) excluído(s) definitivamente; ${removed} vínculo(s) removido(s)`);
      } else {
        toast.success(`${removed} vínculo(s) removido(s) da turma`);
      }
      setDeleteDialog(null);
      await refreshStudents();
    } catch (e) {
      toast.error(fmtError(e));
    } finally {
      setDeleting(false);
    }
  };

  const openStudentEditor = (student) => {
    setEditStudent(student);
    setEditName(student.name || "");
    setEditCpf(String(student.cpf || "").replace(/\D/g, "").slice(0, 11));
    setEditEmail(student.email || "");
    setEditSchoolIds((student.schools || []).map((item) => item.id));
    setEditClassIds((student.classes || []).map((item) => item.id));
  };

  const toggleEditValue = (setter, id) => {
    setter((current) => current.includes(id)
      ? current.filter((value) => value !== id)
      : [...current, id]);
  };

  const saveStudent = async () => {
    if (!editStudent) return;
    const cleanedEditCpf = editCpf.replace(/\D/g, "");
    if (!editName.trim() || (cleanedEditCpf && cleanedEditCpf.length !== 11)) {
      toast.error("Informe o nome e, se houver CPF, os 11 dígitos");
      return;
    }
    if (!editSchoolIds.length && !editClassIds.length) {
      toast.error("Selecione ao menos uma escola ou turma");
      return;
    }
    setSavingStudent(true);
    try {
      await api.put(`/students/${editStudent.id}`, {
        name: editName.trim(),
        cpf: cleanedEditCpf || null,
        email: editEmail.trim() || null,
        school_ids: editSchoolIds,
        class_ids: editClassIds,
      });
      toast.success("Dados do aluno atualizados");
      setEditStudent(null);
      await refreshStudents();
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setSavingStudent(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-8 py-10">
      <div className="mb-8 border-b border-border pb-6">
        <div className="label-eyebrow mb-2">gestão · alunos</div>
        <h1 className="font-display text-5xl font-black tracking-tighter leading-none">Alunos.</h1>
        <p className="text-muted-foreground mt-3 text-sm max-w-[680px]">
          O aluno pode pertencer a várias turmas. Os semestres são organizados pelas provas realizadas e aparecem no histórico individual.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <div className="label-eyebrow mb-1">turma</div>
          <Select value={classId} onValueChange={(value) => resetStudentFilter(setClassId, value)}>
            <SelectTrigger data-testid="filter-class" className="rounded-none"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as turmas</SelectItem>
              {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="label-eyebrow mb-1">buscar</div>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="nome ou CPF..." value={search} onChange={e => resetStudentFilter(setSearch, e.target.value)}
              data-testid="student-search" className="pl-9 rounded-none font-mono-data" />
          </div>
        </div>
      </div>

      <div className="border border-border bg-card p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-end gap-5 justify-between">
          <div className="max-w-[680px]">
            <div className="label-eyebrow mb-1">importação em lote (xlsx)</div>
            <div className="text-sm text-muted-foreground">
              Escolha o modelo completo ou o modelo simples. No simples, apenas nome e turma são obrigatórios; CPF e e-mail podem ser incluídos depois, sem criar conta de acesso automaticamente.
            </div>
          </div>
          <div className="flex flex-col gap-3 min-w-[320px]">
            <div ref={schoolSelectionRef} tabIndex={-1} data-testid="students-import-schools">
              <div className="label-eyebrow mb-2">vincular alunos à escola</div>
              {availableSchools.length === 1 ? (
                <div className="border border-border px-3 py-2 text-sm font-mono-data">{availableSchools[0].name} · automático</div>
              ) : (
                <div className="border border-border p-3 grid gap-2 max-h-36 overflow-y-auto">
                  {availableSchools.map((school) => (
                    <label key={school.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={importStudentSchoolIds.includes(school.id)}
                        onChange={() => toggleSchool(setImportStudentSchoolIds, school.id)} />
                      <span>{school.name}</span>
                    </label>
                  ))}
                </div>
              )}
              {loadingImportOptions ? <p className="text-xs mt-2" role="status">Carregando escolas…</p>
                : importOptionsError ? <div role="alert" className="text-sm text-destructive mt-2">{importOptionsError} <Button type="button" variant="outline" onClick={load}>Tentar novamente</Button></div>
                : !availableSchools.length ? <p role="alert" className="text-sm text-destructive mt-2">Nenhuma escola disponível para importar alunos. Confira seus vínculos na administração.</p>
                : !importStudentSchoolIds.length && <p role="status" className="text-sm text-primary mt-2">Selecione ao menos uma escola acima para importar os alunos.</p>}
            </div>
            {importSchools.length > 1 && (
              <div>
                <div className="label-eyebrow mb-1">escola para criar turmas novas</div>
                <Select value={importSchoolId} onValueChange={setImportSchoolId}>
                  <SelectTrigger className="rounded-none"><SelectValue placeholder="Selecione se houver turma nova..." /></SelectTrigger>
                  <SelectContent>
                    {importSchools.map((school) => <SelectItem key={school.id} value={school.id}>{school.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button data-testid="students-template-download" variant="outline" onClick={() => downloadTemplate("strict")}
                className="rounded-none border-border font-mono-data uppercase tracking-widest text-xs">
                <DownloadSimple size={14} weight="bold" className="mr-2" /> Modelo completo
              </Button>
              <input id="students-upload-input" type="file" ref={fileRef} accept=".xlsx"
                onChange={(e) => e.target.files?.[0] && importXlsx(e.target.files[0])}
                className="sr-only" data-testid="students-upload-input" />
                <Button type="button" data-testid="students-upload-btn" onClick={() => openImportFile("strict")}
                  disabled={importing || loadingImportOptions} className="rounded-none font-mono-data uppercase tracking-widest text-xs">
                  <UploadSimple size={14} weight="bold" className="mr-2" />
                  {importingMode === "strict" ? "Importando…" : "Importar completo"}
                </Button>
              <Button data-testid="students-simple-template-download" variant="outline" onClick={() => downloadTemplate("name_class_only")}
                className="rounded-none border-primary text-primary font-mono-data uppercase tracking-widest text-xs">
                <DownloadSimple size={14} weight="bold" className="mr-2" /> Modelo nome + turma
              </Button>
              <input id="students-simple-upload-input" type="file" ref={simpleFileRef} accept=".xlsx"
                onChange={(e) => e.target.files?.[0] && importXlsx(e.target.files[0], null, "name_class_only")}
                className="sr-only" data-testid="students-simple-upload-input" />
                <Button type="button" data-testid="students-simple-upload-btn" variant="outline" onClick={() => openImportFile("name_class_only")}
                  disabled={importing || loadingImportOptions} className="rounded-none border-primary text-primary font-mono-data uppercase tracking-widest text-xs">
                  <UploadSimple size={14} weight="bold" className="mr-2" />
                  {importingMode === "name_class_only" ? "Importando…" : "Importar nome + turma"}
                </Button>
              <Button data-testid="students-missing-email-download" variant="outline"
                disabled={exportingMissingEmails || loadingStudents || totalStudents === 0}
                onClick={downloadMissingEmails}
                className="rounded-none border-primary text-primary font-mono-data uppercase tracking-widest text-xs">
                <DownloadSimple size={14} weight="bold" className="mr-2" />
                {exportingMissingEmails ? "Exportando…" : "Exportar alunos sem e-mail"}
              </Button>
              <input id="students-email-upload-input" type="file" ref={emailFileRef} accept=".xlsx"
                onChange={(e) => e.target.files?.[0] && importStudentEmails(e.target.files[0])}
                className="sr-only" data-testid="students-email-upload-input" />
              {importingEmails ? (
                <Button data-testid="students-email-upload-btn" variant="outline" disabled className="rounded-none border-primary text-primary font-mono-data uppercase tracking-widest text-xs">
                  <UploadSimple size={14} weight="bold" className="mr-2" />Atualizando e-mails…
                </Button>
              ) : (
                <Button asChild data-testid="students-email-upload-btn" variant="outline" className="rounded-none border-primary text-primary font-mono-data uppercase tracking-widest text-xs">
                  <label htmlFor="students-email-upload-input" className="cursor-pointer"><UploadSimple size={14} weight="bold" className="mr-2" />Reimportar e-mails</label>
                </Button>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              No modelo simples, a segurança contra duplicidade usa nome normalizado dentro de cada turma: se houver mais de um aluno com o mesmo nome nas turmas selecionadas, a linha é interrompida para revisão; o sistema nunca mescla automaticamente alguém de outra turma. Esses alunos ficam sem dados de acesso: não recebem convite e não fazem login até receberem CPF/e-mail. Para completar e-mails: baixe “Sem e-mail”, preencha somente a coluna amarela e use “Reimportar e-mails”. Turmas e escolas não serão alteradas.
            </div>
          </div>
        </div>
        <div className="mt-3 text-xs font-mono-data text-muted-foreground">
          {importSchools.length === 1
            ? `novas turmas serão criadas automaticamente em ${importSchools[0].name}`
            : importSchoolId
              ? "novas turmas serão criadas na escola selecionada"
              : "se houver turma nova e você administrar mais de uma escola, o sistema perguntará a escola de destino"}
        </div>
      </div>

      {importSummary && (
        <div className="border border-primary/40 bg-primary/5 p-5 mb-6" data-testid="students-import-summary">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="label-eyebrow">resultado da última importação</div>
              <div className="font-display text-xl font-black mt-1">
                {importSummary.simple ? "Cadastro provisório: nome + turma" : "Cadastro completo de alunos"}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setImportSummary(null)} className="rounded-none text-muted-foreground">Fechar resumo</Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 font-mono-data text-sm">
            <div><div className="text-muted-foreground text-xs">novos</div><b>{importSummary.created}</b></div>
            <div><div className="text-muted-foreground text-xs">reutilizados</div><b>{importSummary.reused}</b></div>
            <div><div className="text-muted-foreground text-xs">matrículas</div><b>{importSummary.enrolled}</b></div>
            <div><div className="text-muted-foreground text-xs">sem CPF</div><b>{importSummary.createdWithoutCpf}</b></div>
            <div><div className="text-muted-foreground text-xs">ignoradas</div><b>{importSummary.skipped}</b></div>
          </div>
          {!!importSummary.warnings.length && (
            <div className="mt-4 text-sm text-[hsl(var(--warning))]">{importSummary.warnings.slice(0, 5).map((message) => <div key={message}>• {message}</div>)}</div>
          )}
          {!!importSummary.errors.length && (
            <div className="mt-4 border border-destructive/35 bg-destructive/5 p-3 text-sm text-destructive">
              <div className="font-semibold mb-1">Linhas que precisam de revisão</div>
              {importSummary.errors.slice(0, 8).map((message) => <div key={message}>• {message}</div>)}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2 mb-4">
        <Button variant="outline" disabled={loadingStudents || !students.length}
          onClick={() => setSelectedStudentIds(selectedStudentIds.length === students.length ? [] : students.map((item) => item.id))}
          className="rounded-none font-mono-data uppercase tracking-widest text-xs" data-testid="select-all-students">
          {selectedStudentIds.length === students.length && students.length ? "Limpar seleção" : "Selecionar esta página"}
        </Button>
        <Button disabled={loadingStudents || !selectedAccountStudentIds.length || creatingAccounts}
          onClick={createSelectedAccounts}
          className="rounded-none font-mono-data uppercase tracking-widest text-xs" data-testid="create-student-users">
          <UserPlus size={14} weight="bold" className="mr-2" />
          {creatingAccounts ? "Criando usuários…" : `Criar usuários (${selectedAccountStudentIds.length})`}
        </Button>
        <Button variant="outline" disabled={loadingStudents || !invitationStudentIds.length || sendingInvitations}
          onClick={sendPendingInvitations}
          className="rounded-none border-primary text-primary font-mono-data uppercase tracking-widest text-xs"
          data-testid="send-student-invitations">
          <EnvelopeSimple size={14} weight="bold" className="mr-2" />
          {sendingInvitations ? "Enviando convites…" : `Enviar convites pendentes (${invitationStudentIds.length})`}
        </Button>
        <Button variant="outline" disabled={loadingStudents || !activeClassId || students.length === 0}
          onClick={() => setDeleteDialog({ mode: "all" })}
          className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground font-mono-data uppercase tracking-widest text-xs">
          <Trash size={14} weight="bold" className="mr-2" /> Apagar todos da turma
        </Button>
        <Dialog open={open} onOpenChange={handleNewDialog}>
          <DialogTrigger asChild>
            <Button className="rounded-none font-mono-data uppercase tracking-widest text-xs" data-testid="new-student-btn">
              <UserPlus size={14} weight="bold" className="mr-2" /> Novo aluno
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none" aria-describedby="ns-desc">
            <DialogHeader><DialogTitle>Novo aluno</DialogTitle></DialogHeader>
            <p id="ns-desc" className="sr-only">Cadastrar aluno</p>
            <div className="space-y-3">
              <Input placeholder="Nome do aluno" value={name} onChange={e => setName(e.target.value)}
                data-testid="new-student-name" className="rounded-none" />
              <Input placeholder="CPF (11 dígitos)" value={cpf} onChange={e => setCpf(e.target.value.replace(/\D/g, ""))}
                data-testid="new-student-cpf" maxLength={11} className="rounded-none font-mono-data" />
              <Input placeholder="E-mail de acesso (opcional)" type="email" value={email} onChange={e => setEmail(e.target.value)}
                data-testid="new-student-email" className="rounded-none font-mono-data" />
              <div>
                <div className="label-eyebrow mb-2">escola do aluno</div>
                {availableSchools.length === 1 ? (
                  <div className="border border-border px-3 py-2 text-sm font-mono-data">{availableSchools[0].name} · automático</div>
                ) : (
                  <div className="border border-border p-3 grid gap-2 max-h-40 overflow-y-auto">
                    {availableSchools.map((school) => (
                      <label key={school.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={newSchoolIds.includes(school.id)}
                          onChange={() => toggleSchool(setNewSchoolIds, school.id)} />
                        <span>{school.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <div className="label-eyebrow mb-1">turma (opcional)</div>
                <Select value={newClassId || "none"} onValueChange={(value) => setNewClassId(value === "none" ? "" : value)}>
                  <SelectTrigger className="rounded-none"><SelectValue placeholder="Sem turma por enquanto" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem turma por enquanto</SelectItem>
                    {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button className="rounded-none" onClick={create} disabled={!name || cpf.length !== 11 || !newSchoolIds.length} data-testid="new-student-save">Cadastrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {studentPagination}
      <p className="text-xs text-muted-foreground mb-3">Seleção e convites pendentes se referem aos alunos desta página. Exportações e exclusão da turma mantêm o escopo indicado em cada ação.</p>
      <div className="border border-border bg-card">
        {loadingStudents ? (
          <div className="p-16 text-center label-eyebrow">carregando alunos…</div>
        ) : students.length === 0 ? (
          <div className="p-16 text-center label-eyebrow">
            {activeClassId ? "nenhum aluno nesta turma" : "nenhum aluno cadastrado"}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {students.map(s => (
              <div key={s.id} data-testid={`student-row-${s.id}`}
                role="button" tabIndex={0}
                onClick={() => navigate(`/students/${s.id}`)}
                onKeyDown={(event) => event.key === "Enter" && navigate(`/students/${s.id}`)}
                className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-muted/20 transition-colors cursor-pointer group">
                <div className="flex items-center gap-4 min-w-0">
                  <input type="checkbox" checked={selectedStudentIds.includes(s.id)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => toggleStudentSelection(s.id)}
                    aria-label={`Selecionar ${s.name}`} data-testid={`select-student-${s.id}`} />
                  <StudentIcon size={20} weight="duotone" className="text-primary shrink-0" />
                  <div className="min-w-0">
                    <div className="font-mono-data font-semibold text-sm truncate">{s.name}</div>
                    <div className="font-mono-data text-xs text-muted-foreground">
                      {s.cpf ? `CPF ${s.cpf}` : "cadastro provisório · sem CPF"}
                    </div>
                    {s.email && <div className="font-mono-data text-xs text-muted-foreground">{s.email}</div>}
                    <div className={`inline-flex mt-1 border px-2 py-0.5 text-[10px] uppercase tracking-widest ${s.cpf_pending ? "border-[hsl(var(--warning))]/50 text-[hsl(var(--warning))]" : s.has_user_account ? "border-[hsl(var(--success))]/50 text-[hsl(var(--success))]" : "border-border text-muted-foreground"}`}>
                      {s.cpf_pending
                        ? "cadastro provisório · informe CPF para liberar acesso"
                        : s.has_user_account
                        ? (s.user_account_active
                          ? "usuário ativo"
                          : s.student_invitation_sent_at ? "convite enviado · aguardando ativação" : "conta pronta · convite não enviado")
                        : "sem usuário"}
                    </div>
                    {!!s.schools?.length && (
                      <div className="text-xs text-primary mt-1 truncate">
                        {s.schools.map(item => item.name).join(" · ")}
                      </div>
                    )}
                    {!!s.classes?.length && (
                      <div className="text-xs text-muted-foreground mt-1 truncate">
                        {s.classes.map(item => item.name).join(" · ")}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {user?.is_admin && !s.cpf_pending && s.has_user_account && !s.user_account_active && s.email && (
                    <Button
                      variant="outline"
                      size="sm"
                      title="Reenviar confirmação"
                      disabled={resendingStudentId === s.id}
                      onClick={(event) => { event.stopPropagation(); resendStudentConfirmation(s); }}
                      className="rounded-none border-primary text-primary font-mono-data uppercase tracking-widest text-[10px]"
                      data-testid={`resend-student-confirmation-${s.id}`}>
                      <EnvelopeSimple size={13} weight="bold" className="mr-1" />
                      {resendingStudentId === s.id ? "Enviando…" : "Reenviar confirmação"}
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" title="Alterar aluno"
                    onClick={(event) => { event.stopPropagation(); openStudentEditor(s); }}
                    className="rounded-none text-muted-foreground hover:text-primary">
                    <PencilSimple size={17} weight="bold" />
                  </Button>
                  <Button variant="ghost" size="icon" title="Apagar aluno"
                    onClick={(event) => { event.stopPropagation(); setDeleteDialog({ mode: "one", student: s }); }}
                    className="rounded-none text-muted-foreground hover:text-destructive">
                    <Trash size={17} weight="bold" />
                  </Button>
                  <ArrowRight size={17} weight="bold" className="text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {studentPagination}
      <Dialog open={Boolean(editStudent)} onOpenChange={(value) => !value && !savingStudent && setEditStudent(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[92vh] overflow-y-auto overscroll-contain" aria-describedby="edit-student-description">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-black tracking-tight">Alterar aluno</DialogTitle>
          </DialogHeader>
          <p id="edit-student-description" className="text-sm text-muted-foreground">
            Atualize os dados cadastrais e os vínculos de escola e turma.
          </p>
          <div className="space-y-4">
            <div>
              <div className="label-eyebrow mb-1">nome completo</div>
              <Input value={editName} onChange={(event) => setEditName(event.target.value)} className="rounded-none" data-testid="edit-student-name" />
            </div>
            <div>
              <div className="label-eyebrow mb-1">cpf (opcional no cadastro provisório)</div>
              <Input
                value={editCpf}
                onChange={(event) => setEditCpf(event.target.value.replace(/\D/g, "").slice(0, 11))}
                inputMode="numeric"
                maxLength={11}
                className="rounded-none font-mono-data"
                data-testid="edit-student-cpf"
              />
              <div className="mt-1 text-xs text-muted-foreground">Informe os 11 dígitos quando o CPF estiver disponível. Sem CPF, o aluno permanece sem conta de acesso.</div>
            </div>
            <div>
              <div className="label-eyebrow mb-1">e-mail de acesso</div>
              <Input type="email" value={editEmail} onChange={(event) => setEditEmail(event.target.value)}
                placeholder="aluno@escola.edu.br" className="rounded-none font-mono-data" data-testid="edit-student-email" />
              <div className="mt-1 text-xs text-muted-foreground">Ao informar o e-mail, o sistema cria ou atualiza a conta sem enviar mensagem. Use depois o botão coletivo “Enviar convites pendentes”.</div>
            </div>
            <div>
              <div className="label-eyebrow mb-2">escolas</div>
              <div className="border border-border p-3 grid gap-2 max-h-44 overflow-y-auto">
                {availableSchools.map((school) => (
                  <label key={school.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={editSchoolIds.includes(school.id)} onChange={() => toggleEditValue(setEditSchoolIds, school.id)} />
                    <span>{school.name}</span>
                  </label>
                ))}
                {!availableSchools.length && <div className="text-sm text-muted-foreground">Nenhuma escola disponível.</div>}
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-2">turmas</div>
              <div className="border border-border p-3 grid gap-2 max-h-52 overflow-y-auto">
                {classes.map((classItem) => (
                  <label key={classItem.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={editClassIds.includes(classItem.id)} onChange={() => toggleEditValue(setEditClassIds, classItem.id)} />
                    <span>{classItem.name}</span>
                  </label>
                ))}
                {!classes.length && <div className="text-sm text-muted-foreground">Nenhuma turma disponível.</div>}
              </div>
              <div className="text-xs text-muted-foreground mt-1">As turmas selecionadas também vinculam o aluno às escolas correspondentes.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" disabled={savingStudent} onClick={() => setEditStudent(null)}>Cancelar</Button>
            <Button className="rounded-none" disabled={savingStudent} onClick={saveStudent} data-testid="edit-student-save">
              {savingStudent ? "Salvando…" : "Salvar alterações"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deleteDialog)} onOpenChange={(value) => !value && !deleting && setDeleteDialog(null)}>
        <DialogContent className="rounded-none max-w-xl" aria-describedby="delete-student-description">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-black tracking-tight">
              {deleteDialog?.mode === "all" ? "Apagar todos os alunos?" : `Apagar ${deleteDialog?.student?.name || "aluno"}?`}
            </DialogTitle>
          </DialogHeader>
          <div id="delete-student-description" className="space-y-3 text-sm text-muted-foreground">
            {activeClassId && (
              <p>
                <strong>Remover da turma</strong> apaga somente o vínculo com {selectedClass?.name || "esta turma"}. O aluno continua cadastrado nas outras turmas e todo o histórico de provas é preservado.
              </p>
            )}
            {(deleteDialog?.mode === "one" || user?.is_admin) && (
              <p className="border border-destructive/50 bg-destructive/5 p-3 text-foreground">
                <strong>Excluir definitivamente</strong> remove o cadastro e todos os vínculos do aluno. As correções já processadas são preservadas como cartões não vinculados.
              </p>
            )}
            {deleteDialog?.mode === "all" && search && (
              <p className="font-mono-data text-xs">A ação em massa considera todos os alunos da turma, não apenas o resultado da busca.</p>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="rounded-none" disabled={deleting} onClick={() => setDeleteDialog(null)}>Cancelar</Button>
            {activeClassId && (
              <Button variant="outline" className="rounded-none" disabled={deleting} onClick={() => runDelete(false)}>
                {deleting ? "Processando…" : "Remover da turma"}
              </Button>
            )}
            {(deleteDialog?.mode === "one" || user?.is_admin) && (
              <Button variant="destructive" className="rounded-none" disabled={deleting} onClick={() => runDelete(true)}>
                {deleting ? "Processando…" : "Excluir definitivamente"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(resolutionDialog)} onOpenChange={(openDialog) => !openDialog && cancelResolution()}>
        <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="class-resolution-description">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-black tracking-tight">Turma precisa ser resolvida</DialogTitle>
          </DialogHeader>
          <p id="class-resolution-description" className="text-sm text-muted-foreground">
            {resolutionDialog?.message || "Uma ou mais turmas da planilha precisam de confirmação."}
            Para cada nome abaixo, escolha uma turma existente ou crie uma nova com o mesmo nome.
          </p>

          <div className="space-y-4 mt-2">
            {(resolutionDialog?.issues || []).map((issue) => {
              const resolution = classResolutions[issue.key] || {};
              return (
                <div key={issue.key} className="border border-border p-4">
                  <div className="font-mono-data font-semibold">“{issue.name}”</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Linha(s): {(issue.rows || []).join(", ")}
                    {issue.students?.length ? ` · Aluno(s): ${issue.students.slice(0, 4).join(", ")}` : ""}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                    <div>
                      <div className="label-eyebrow mb-1">o que deseja fazer?</div>
                      <Select value={resolution.action || ""}
                        onValueChange={(action) => {
                          if (action === "existing") {
                            const preferred = resolutionDialog.availableClasses.find((item) => (issue.candidate_class_ids || []).includes(item.id))
                              || resolutionDialog.availableClasses.find((item) => item.id === activeClassId)
                              || resolutionDialog.availableClasses[0];
                            updateResolution(issue.key, { action, class_id: preferred?.id || "", school_id: undefined });
                          } else {
                            const preferredSchool = resolutionDialog.manageableSchools.find((item) => item.id === importSchoolId)
                              || resolutionDialog.manageableSchools[0];
                            updateResolution(issue.key, { action, school_id: preferredSchool?.id || "", class_id: undefined });
                          }
                        }}>
                        <SelectTrigger className="rounded-none"><SelectValue placeholder="Escolha..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="existing" disabled={!resolutionDialog.availableClasses.length}>Alocar em turma existente</SelectItem>
                          <SelectItem value="create" disabled={!resolutionDialog.manageableSchools.length}>Criar nova turma “{issue.name}”</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {resolution.action === "existing" ? (
                      <div>
                        <div className="label-eyebrow mb-1">turma de destino</div>
                        <Select value={resolution.class_id || ""} onValueChange={(value) => updateResolution(issue.key, { class_id: value })}>
                          <SelectTrigger className="rounded-none"><SelectValue placeholder="Selecione a turma..." /></SelectTrigger>
                          <SelectContent>
                            {resolutionDialog.availableClasses.map((item) => (
                              <SelectItem key={item.id} value={item.id}>{item.label}{item.school_name ? ` · ${item.school_name}` : ""}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div>
                        <div className="label-eyebrow mb-1">escola da nova turma</div>
                        <Select value={resolution.school_id || ""} onValueChange={(value) => updateResolution(issue.key, { school_id: value })}>
                          <SelectTrigger className="rounded-none"><SelectValue placeholder="Selecione a escola..." /></SelectTrigger>
                          <SelectContent>
                            {resolutionDialog.manageableSchools.map((school) => (
                              <SelectItem key={school.id} value={school.id}>{school.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {!resolutionDialog?.manageableSchools?.length && (
            <div className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5 p-3 text-sm">
              Sua conta não possui permissão para criar turmas. Ainda é possível alocar os alunos em uma turma existente.
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={cancelResolution}>Cancelar importação</Button>
            <Button className="rounded-none" disabled={!resolutionsValid || importing} onClick={continueImport}>
              {importing ? "Importando…" : "Confirmar e continuar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
